import api from "@/services/modules/workers";
import referenceApi from "@/services/modules/references";
import {
  SET_ERROR,
  SET_IS_FETCHING_DATA,
  SET_NEW_WORKER,
  SET_WORKER,
  SET_WORKER_APPLICATIONS,
  SET_WORKER_TIMESHEETS,
  SET_INTERMEDIARY_COMPANY,
  SET_WORKERS,
  SET_IS_FETCHING_APPLICATIONS,
  SET_IS_FETCHING_TIMESHEETS,
  SET_WORKER_COMPLIANCE_FILE,
  SET_WORKER_ID,
  CLEAR_INTERMEDIARY_COMPANY,
  CLEAR_CURRENT_WORKER,
  SET_WORKER_FILTERS,
  REMOVE_WORKER_COMPLIANCE_FILE,
  SET_WORKER_COMPLIANCE_FILES,
  FETCH_WORKER_COMPLIANCE_DOCS,
  SET_SECTIONS,
  SET_IS_FETCHING_SECTIONS
} from "./mutation-types";
import {
  ADD_NEW_WORKER,
  DELETE_WORKER,
  GET_ALL_WORKERS,
  GET_WORKER,
  GET_WORKER_APPLICATIONS,
  GET_WORKER_TIMESHEETS,
  GET_INTERMEDIARY_COMPANY,
  UPDATE_WORKER,
  ADD_NEW_REFERENCE,
  DELETE_REFERENCE,
  DELETE_WORKER_CV,
  DELETE_WORKER_COMPLIANCE_DOC,
  GET_SECTIONS,
  CREATE_SECTION,
  UPDATE_SECTION,
  DELETE_SECTION
} from "./action-types";
import {
  addGlobalMessage,
  errorHandler,
  handleWithErrorMessage,
  handleWithGlobalMessage
} from "@/services/utils";
import { setStoreValue } from "@/store/utils";
import { isMockedDataEnabled } from "@/utils/mocked-data";
import { MOCKED_WORKERS_STATE } from "@/constants/workers";
import { first } from "lodash";

export const WORKERS_NAMESPACE = "workers";

const state = {
  workers: [],
  references: [],
  currentWorker: {},
  intermediaryCompany: {},
  currentWorkerId: null,
  applications: [],
  timesheets: [],
  isFetchingData: false,
  isFetchingApplications: false,
  isFetchingTimesheets: false,
  fetchingError: null,
  workerFilters: {},
  sections: [],
  isFetchingSections: false
};

const actions = {
  async [GET_ALL_WORKERS]({ commit, dispatch }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getAllWorkers(params),
      setLoadingFunction: isLoading => commit(SET_IS_FETCHING_DATA, isLoading),
      setDataFunction: ({ data }) => commit(SET_WORKERS, data)
    });
  },
  async [GET_WORKER_APPLICATIONS]({ commit, dispatch }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getApplications({ params }),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_APPLICATIONS, isLoading),
      setDataFunction: ({ data }) => commit(SET_WORKER_APPLICATIONS, data)
    });
  },
  async [GET_WORKER_TIMESHEETS]({ commit, dispatch }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getTimesheets({ params }),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_TIMESHEETS, isLoading),
      setDataFunction: ({ data }) => commit(SET_WORKER_TIMESHEETS, data)
    });
  },
  async [ADD_NEW_WORKER]({ commit, dispatch }, newWorker) {
    try {
      commit(SET_IS_FETCHING_DATA, true);
      const { data } = await api({ commit }).addNewWorker(newWorker);
      commit(SET_WORKER_ID, data.id);
      return data;
    } catch (exception) {
      errorHandler(dispatch, exception);
    } finally {
      commit(SET_IS_FETCHING_DATA, false);
    }
  },
  async [GET_WORKER]({ commit, dispatch }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getWorker(id),
      setLoadingFunction: isLoading => commit(SET_IS_FETCHING_DATA, isLoading),
      setDataFunction: ({ data }) => {
        commit(SET_WORKER_ID, data.id);
        commit(SET_WORKER, data);
        commit(SET_SECTIONS, data.sections);
      }
    });
  },
  async [UPDATE_WORKER]({ commit, dispatch }, { updatedWorker, hideMessage }) {
    try {
      const { data, meta } = await api({ commit }).updateWorker(updatedWorker);
      if (!hideMessage) {
        addGlobalMessage(dispatch, { type: "success", meta });
      }
      commit(SET_WORKER, data);
      if (data?.id) {
        const { data: list } = await api().getWorkerComplianceDocuments(
          data.id
        );
        commit(
          SET_WORKER_COMPLIANCE_FILES,
          Array.isArray(list) ? list : list?.data || []
        );
      }
      return data;
    } catch (exception) {
      errorHandler(dispatch, exception);
    }
  },
  async [DELETE_WORKER]({ commit, dispatch }, { id, hideMessage }) {
    if (!hideMessage) {
      return handleWithGlobalMessage(
        () => api({ commit }).deleteWorker(id),
        dispatch
      );
    }
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).deleteWorker(id)
    });
  },
  async [GET_INTERMEDIARY_COMPANY]({ commit, dispatch }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getIntermediaryCompany(id),
      setDataFunction: ({ data, included }) => {
        if (data === null) {
          commit(SET_INTERMEDIARY_COMPANY, {});
          return;
        }
        data.attributes.addresses = [];
        if (included) {
          const { id, type, attributes } = first(included);
          data.attributes.addresses = [{ id, type, ...attributes }];
        }

        commit(SET_INTERMEDIARY_COMPANY, { ...data.attributes, id: data.id });
      }
    });
  },
  async [ADD_NEW_REFERENCE]({ commit, dispatch }, newReference) {
    try {
      const { data } = await referenceApi({ commit }).addNewReference(
        newReference
      );
      return data;
    } catch (exception) {
      errorHandler(dispatch, exception);
    }
  },
  async [DELETE_REFERENCE]({ commit, dispatch }, id) {
    try {
      await referenceApi({ commit }).deleteReference(id);
      return id;
    } catch (exception) {
      errorHandler(dispatch, exception);
    }
  },
  async [DELETE_WORKER_CV]({ dispatch }, id) {
    try {
      await api().deleteWorkerCV(id);
    } catch (exception) {
      errorHandler(dispatch, exception.response.data);
    }
  },
  async [DELETE_WORKER_COMPLIANCE_DOC](
    { commit, dispatch },
    { workerId, docId }
  ) {
    try {
      await api().deleteWorkerComplianceDocumentById(workerId, docId);
      commit(REMOVE_WORKER_COMPLIANCE_FILE, docId);
      return docId;
    } catch (exception) {
      errorHandler(dispatch, exception.response?.data || exception);
    }
  },
  async [FETCH_WORKER_COMPLIANCE_DOCS]({ commit }, workerId) {
    const res = await api().getWorkerComplianceDocuments(workerId);
    const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
    commit(SET_WORKER_COMPLIANCE_FILES, items);
    return items;
  },
  async [GET_SECTIONS]({ commit, dispatch }, { id }) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getSections({ id }),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_SECTIONS, isLoading),
      setDataFunction: ({ data }) => commit(SET_SECTIONS, data)
    });
  },
  async [CREATE_SECTION]({ commit, dispatch }, { id, section }) {
    return handleWithGlobalMessage(
      () => api({ commit }).createSection({ id, section }),
      dispatch
    );
  },
  async [UPDATE_SECTION]({ commit, dispatch }, { id, oldSection, section }) {
    return handleWithGlobalMessage(
      () => api({ commit }).updateSection({ id, oldSection, section }),
      dispatch
    );
  },
  async [DELETE_SECTION]({ commit, dispatch }, { id, section }) {
    return handleWithGlobalMessage(
      () => api({ commit }).deleteSection({ id, section }),
      dispatch
    );
  }
};

const mutations = {
  [SET_WORKERS]: setStoreValue("workers"),
  [SET_WORKER](state, worker) {
    const prevFiles = state.currentWorker?.complianceSupportingFiles || [];

    let files;
    if (Array.isArray(worker.complianceSupportingFiles)) {
      files = worker.complianceSupportingFiles;
    } else if (worker.complianceSupportingFile) {
      files = prevFiles.length ? prevFiles : [worker.complianceSupportingFile];
    } else {
      files = prevFiles;
    }
    state.currentWorker = {
      ...state.currentWorker,
      ...worker,
      complianceSupportingFiles: files
    };
  },
  [SET_WORKER_ID]: setStoreValue("currentWorkerId"),
  [SET_NEW_WORKER](state, worker) {
    state.workers.push(worker);
  },
  [SET_WORKER_APPLICATIONS]: setStoreValue("applications"),
  [SET_WORKER_TIMESHEETS]: setStoreValue("timesheets"),
  [SET_IS_FETCHING_DATA]: setStoreValue("isFetchingData"),
  [SET_IS_FETCHING_APPLICATIONS]: setStoreValue("isFetchingApplications"),
  [SET_IS_FETCHING_TIMESHEETS]: setStoreValue("isFetchingTimesheets"),
  [SET_ERROR]: setStoreValue("fetchingError"),
  [SET_INTERMEDIARY_COMPANY]: setStoreValue("intermediaryCompany"),
  [SET_WORKER_COMPLIANCE_FILE](state, file) {
    if (!Array.isArray(state.currentWorker.complianceSupportingFiles)) {
      state.currentWorker.complianceSupportingFiles = [];
    }
    const exists = state.currentWorker.complianceSupportingFiles.some(
      f => String(f.id) === String(file.id)
    );
    if (!exists) state.currentWorker.complianceSupportingFiles.push(file);

    // Optional backward-compat:
    state.currentWorker.complianceSupportingFile = file;
  },
  [SET_WORKER_COMPLIANCE_FILES](state, files) {
    state.currentWorker.complianceSupportingFiles = Array.isArray(files)
      ? files
      : [];
  },
  [REMOVE_WORKER_COMPLIANCE_FILE](state, docId) {
    if (!Array.isArray(state.currentWorker.complianceSupportingFiles)) return;

    state.currentWorker.complianceSupportingFiles = state.currentWorker.complianceSupportingFiles.filter(
      file => String(file.id) !== String(docId)
    );

    // Keep backward compatibility: reset single file if no files left
    state.currentWorker.complianceSupportingFile =
      state.currentWorker.complianceSupportingFiles.length > 0
        ? state.currentWorker.complianceSupportingFiles[0]
        : null;
  },
  [CLEAR_INTERMEDIARY_COMPANY](state) {
    state.intermediaryCompany = {};
  },
  [CLEAR_CURRENT_WORKER](state) {
    state.currentWorker = {};
  },
  [SET_WORKER_FILTERS]: setStoreValue("workerFilters"),
  [SET_SECTIONS]: setStoreValue("sections"),
  [SET_IS_FETCHING_SECTIONS]: setStoreValue("isFetchingSections")
};

export default {
  namespaced: true,
  state: isMockedDataEnabled() ? MOCKED_WORKERS_STATE : state,
  actions: isMockedDataEnabled() ? {} : actions,
  mutations: isMockedDataEnabled() ? {} : mutations
};
